import { useState, useEffect, useCallback } from 'react';
import { Trash2, Edit3, Save, X, Plus, ChevronDown } from 'lucide-react';
import styled, { keyframes } from 'styled-components';

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;
// const backendUrl = 'http://127.0.0.1:8000';

/* ─────────────────────────── Keyframes ─────────────────────────── */
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

/* ─────────────────────────── Styled Components ─────────────────────────── */

const PageWrapper = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 20px 16px;
  background-color: #ffffff;
  border-radius: 8px;
`;

/* Header row */
const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const PageTitle = styled.h1`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: ${({ disabled }) => (disabled ? '#9ca3af' : '#2563eb')};
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #1d4ed8;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e2e8f0;
  margin-bottom: 16px;
`;

/* Error / status banners */
const ErrorBanner = styled.p`
  padding: 12px 16px;
  color: #ef4444;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const CenteredBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 56px 0;
  gap: 12px;
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid #e2e8f0;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

/* Accordion */
const AccordionList = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 20px;
`;

const AccordionItem = styled.div`
  & + & {
    border-top: 1px solid #e2e8f0;
  }
`;

const AccordionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  background: #ffffff;
  border: none;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  text-align: left;
  transition: background 0.15s;

  &:hover {
    background: ${({ $disabled }) => ($disabled ? '#ffffff' : '#f8fafc')};
  }
`;

const IndexDot = styled.span`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${({ $open }) => ($open ? '#2563eb' : '#e2e8f0')};
  color: ${({ $open }) => ($open ? '#ffffff' : '#64748b')};
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;
`;

const HeadingText = styled.span`
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  flex-shrink: 0;
`;

const IconBtn = styled.button`
  padding: 6px;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  color: ${({ $color, disabled }) => (disabled ? '#9ca3af' : $color || '#374151')};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;

  &:hover:not(:disabled) {
    background: ${({ $hoverBg }) => $hoverBg || '#f3f4f6'};
  }
`;

const Chevron = styled(ChevronDown)`
  flex-shrink: 0;
  color: #94a3b8;
  transition: transform 0.25s;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

/* Accordion body */
const AccordionBody = styled.div`
  max-height: ${({ $open }) => ($open ? '1200px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
  background: #fafafa;
  border-top: ${({ $open }) => ($open ? '1px solid #e2e8f0' : 'none')};
`;

const BodyInner = styled.div`
  padding: 16px;
`;

/* Two-column layout inside body */
const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
`;

const ColLabel = styled.p`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 8px 0;
`;

const ContentDiv = styled.div`
  font-size: 18px;
  color: #374151;
  line-height: 1.6;

  ul {
    margin: 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 4px;
  }
`;

/* Edit mode inputs */
const StyledInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  outline: none;
  margin-bottom: 10px;
  transition: border 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
`;

const EditActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #e2e8f0;
`;

const SaveBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${({ disabled }) => (disabled ? '#9ca3af' : '#16a34a')};
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #15803d;
  }
`;

const CancelBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${({ disabled }) => (disabled ? '#f3f4f6' : '#f1f5f9')};
  color: #4b5563;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #e2e8f0;
  }
`;

/* Editable points list sub-styles */
const PointRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
`;

const Bullet = styled.span`
  color: #9ca3af;
  font-size: 16px;
  line-height: 1;
  flex-shrink: 0;
`;

const PointInput = styled.input`
  flex: 1;
  padding: 6px 10px;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  outline: none;
  transition: border 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px #3b82f6;
  }
`;

const DeletePointBtn = styled.button`
  padding: 5px;
  color: #ef4444;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background 0.15s;
  flex-shrink: 0;

  &:hover {
    background: #fef2f2;
  }
`;

const AddPointBtn = styled.button`
  font-size: 13px;
  color: #2563eb;
  background: none;
  border: none;
  cursor: pointer;
  font-weight: 500;
  padding: 2px 0;
  transition: color 0.15s;

  &:hover {
    color: #1d4ed8;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 0;
  color: #6b7280;
  font-size: 15px;
`;

/* ─────────────────────────── EditablePointsList component ─────────────────────────── */
const EditablePointsList = ({ language, points, onPointChange, onAddPoint, onDeletePoint }) => (
  <div style={{ marginBottom: '8px' }}>
    {points.map((point, pointIndex) => (
      <PointRow key={`${language}-${pointIndex}`}>
        <Bullet>•</Bullet>
        <PointInput
          type="text"
          value={point}
          onChange={(e) => onPointChange(language, pointIndex, e.target.value)}
          placeholder={`Point ${pointIndex + 1}`}
        />
        <DeletePointBtn onClick={() => onDeletePoint(language, pointIndex)}>
          <Trash2 size={14} />
        </DeletePointBtn>
      </PointRow>
    ))}
    <AddPointBtn onClick={() => onAddPoint(language)}>+ Add Point</AddPointBtn>
  </div>
);

/* ─────────────────────────── Main Component ─────────────────────────── */
const Lightningadvisory = () => {
  const [advisories, setAdvisories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingAdvisoryId, setEditingAdvisoryId] = useState(null);
  const [editableEnglishHeading, setEditableEnglishHeading] = useState('');
  const [editableEnglishPoints, setEditableEnglishPoints] = useState([]);
  const [editableTetumHeading, setEditableTetumHeading] = useState('');
  const [editableTetumPoints, setEditableTetumPoints] = useState([]);
  const [openIndex, setOpenIndex] = useState(0);

  /* ── Fetch ── */
  const fetchAdvisories = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${backendUrl}/lightning/lightning-advisories/`);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}. Response: ${errorText.substring(0, 200)}`);
      }
      const data = await response.json();
      setAdvisories(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to fetch advisories:', e);
      setError('Failed to load advisories. Please try again later.');
      setAdvisories([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdvisories();
  }, [fetchAdvisories]);

  /* ── Helpers ── */
  const parseDescriptionToList = (htmlString) => {
    if (!htmlString) return [];
    const matches = [...htmlString.matchAll(/<li>(.*?)<\/li>/g)];
    return matches.map((match) => match[1]);
  };

  const listToDescriptionHtml = (itemsArray) => {
    if (!itemsArray || itemsArray.length === 0) return '<ul></ul>';
    return `<ul>${itemsArray.map((item) => `<li>${item}</li>`).join('')}</ul>`;
  };

  /* ── Edit handlers ── */
  const handleEdit = (advisory, index) => {
    setEditingAdvisoryId(advisory.id);
    setEditableEnglishHeading(advisory.englishheading || '');
    setEditableEnglishPoints(parseDescriptionToList(advisory.englishdescription || ''));
    setEditableTetumHeading(advisory.tetumheading || '');
    setEditableTetumPoints(parseDescriptionToList(advisory.tetumdescription || ''));
    setOpenIndex(index);
  };

  const handleCancelEdit = () => {
    setEditingAdvisoryId(null);
  };

  const handleSaveEdit = async () => {
    if (editingAdvisoryId === null) return;

    const updatedEnglishDescriptionHtml = listToDescriptionHtml(editableEnglishPoints);
    const updatedTetumDescriptionHtml = listToDescriptionHtml(editableTetumPoints);

    const payload = {
      englishheading: editableEnglishHeading,
      englishdescription: updatedEnglishDescriptionHtml,
      tetumheading: editableTetumHeading,
      tetumdescription: updatedTetumDescriptionHtml,
    };

    setIsLoading(true);
    try {
      const response = await fetch(`${backendUrl}/lightning/lightning-advisories/${editingAdvisoryId}/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorDataText = 'Error details not available in JSON format.';
        try {
          const errorData = await response.json();
          errorDataText = JSON.stringify(errorData);
        } catch (jsonError) {
          errorDataText = await response.text();
        }
        throw new Error(`Failed to save advisory (ID: ${editingAdvisoryId}): ${response.status} - ${errorDataText}`);
      }
      handleCancelEdit();
      fetchAdvisories();
    } catch (e) {
      console.error('Error saving advisory:', e);
      setError(`Failed to save changes for advisory ID ${editingAdvisoryId}: ${e.message}`);
      setIsLoading(false);
    }
  };

  /* ── Point handlers ── */
  const handlePointChange = (language, index, value) => {
    if (language === 'english') {
      const newPoints = [...editableEnglishPoints];
      newPoints[index] = value;
      setEditableEnglishPoints(newPoints);
    } else if (language === 'tetum') {
      const newPoints = [...editableTetumPoints];
      newPoints[index] = value;
      setEditableTetumPoints(newPoints);
    }
  };

  const handleAddPointToList = (language) => {
    if (language === 'english') {
      setEditableEnglishPoints([...editableEnglishPoints, '']);
    } else if (language === 'tetum') {
      setEditableTetumPoints([...editableTetumPoints, '']);
    }
  };

  const handleDeletePoint = (language, index) => {
    if (language === 'english') {
      setEditableEnglishPoints(editableEnglishPoints.filter((_, i) => i !== index));
    } else if (language === 'tetum') {
      setEditableTetumPoints(editableTetumPoints.filter((_, i) => i !== index));
    }
  };

  /* ── Add / Delete advisory ── */
  const handleAddNewAdvisory = async () => {
    const newAdvisoryData = {
      englishheading: 'Write Type',
      englishdescription: '<ul><li>point 1 </li></ul>',
      tetumheading: 'Tipu Hakerek',
      tetumdescription: '<ul><li> foun 1</li></ul>',
    };

    setIsLoading(true);
    try {
      const response = await fetch(`${backendUrl}/lightning/lightning-advisories/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newAdvisoryData),
      });
      if (!response.ok) {
        let errorDataText = 'Error details not available in JSON format.';
        try {
          const errorData = await response.json();
          errorDataText = JSON.stringify(errorData);
        } catch (jsonError) {
          errorDataText = await response.text();
        }
        throw new Error(`Failed to add new advisory: ${response.status} - ${errorDataText}`);
      }
      fetchAdvisories();
    } catch (e) {
      console.error('Error adding new advisory:', e);
      setError(`Failed to add new advisory: ${e.message}`);
      setIsLoading(false);
    }
  };

  const handleDeleteAdvisory = async (advisoryId) => {
    if (!window.confirm('Are you sure you want to delete this advisory section? This action cannot be undone.')) {
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${backendUrl}/lightning/lightning-advisories/${advisoryId}/`, {
        method: 'DELETE',
        headers: {},
      });
      if (!response.ok && response.status !== 204) {
        let errorDataText = 'Error details not available.';
        try {
          const errorData = await response.json();
          errorDataText = JSON.stringify(errorData);
        } catch (jsonError) {
          errorDataText = await response.text();
        }
        throw new Error(`Failed to delete advisory (ID: ${advisoryId}): ${response.status} - ${errorDataText}`);
      }
      fetchAdvisories();
    } catch (e) {
      console.error('Error deleting advisory:', e);
      setError(`Failed to delete advisory (ID: ${advisoryId}): ${e.message}`);
      setIsLoading(false);
    }
  };

  /* ── Accordion toggle ── */
  const handleToggle = (index) => {
    // Prevent toggling away while a different item is being edited
    if (editingAdvisoryId !== null) return;
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  /* ── Loading state (initial, no data yet) ── */
  if (isLoading && advisories.length === 0 && !error) {
    return (
      <PageWrapper>
        <CenteredBox>
          <Spinner />
        </CenteredBox>
      </PageWrapper>
    );
  }

  /* ── Error state (no data at all) ── */
  if (error && advisories.length === 0) {
    return (
      <PageWrapper>
        <CenteredBox>
          <span style={{ fontSize: '28px' }}>⚠️</span>
          <span style={{ color: '#ef4444', fontSize: '15px' }}>{error}</span>
        </CenteredBox>
      </PageWrapper>
    );
  }

  /* ── Main render ── */
  return (
    <PageWrapper>
      {/* Header */}
      <PageHeader>
        <PageTitle>Lightning Advisory</PageTitle>
        <AddButton
          onClick={handleAddNewAdvisory}
          disabled={isLoading || editingAdvisoryId !== null}
        >
          <Plus size={15} />
          Add New Section
        </AddButton>
      </PageHeader>

      <Divider />

      {/* Error banner (has data) */}
      {error && <ErrorBanner>{error}</ErrorBanner>}

      {/* Empty state */}
      {advisories.length === 0 && !isLoading && (
        <EmptyState>
          <p style={{ marginBottom: '16px' }}>No lightning advisories available.</p>
          <AddButton onClick={handleAddNewAdvisory} style={{ margin: '0 auto' }}>
            <Plus size={15} />
            Add New Section
          </AddButton>
        </EmptyState>
      )}

      {/* Accordion */}
      {advisories.length > 0 && (
        <AccordionList>
          {advisories.map((advisory, index) => {
            const isOpen = openIndex === index;
            const isEditing = editingAdvisoryId === advisory.id;
            const otherEditing = editingAdvisoryId !== null && !isEditing;
            const actionsDisabled = isLoading || otherEditing;

            return (
              <AccordionItem key={advisory.id}>
                {/* ── Header ── */}
                <AccordionHeader
                  onClick={() => handleToggle(index)}
                  $disabled={editingAdvisoryId !== null}
                >
                  <IndexDot $open={isOpen}>{index + 1}</IndexDot>

                  <HeadingText>
                    {advisory.englishheading} / {advisory.tetumheading}
                  </HeadingText>

                  {/* Action buttons — stop propagation so they don't toggle accordion */}
                  <HeaderActions onClick={(e) => e.stopPropagation()}>
                    <IconBtn
                      title="Edit"
                      $color="#2563eb"
                      $hoverBg="#eff6ff"
                      disabled={actionsDisabled}
                      onClick={() => handleEdit(advisory, index)}
                    >
                      <Edit3 size={15} />
                    </IconBtn>
                    <IconBtn
                      title="Delete"
                      $color="#ef4444"
                      $hoverBg="#fef2f2"
                      disabled={actionsDisabled}
                      onClick={() => handleDeleteAdvisory(advisory.id)}
                    >
                      <Trash2 size={15} />
                    </IconBtn>
                  </HeaderActions>

                  <Chevron size={16} $open={isOpen ? 1 : 0} />
                </AccordionHeader>

                {/* ── Body ── */}
                <AccordionBody $open={isOpen}>
                  <BodyInner>
                    {isEditing ? (
                      /* ── EDIT MODE ── */
                      <>
                        <TwoCol>
                          {/* English column */}
                          <div>
                            <ColLabel>English</ColLabel>
                            <StyledInput
                              type="text"
                              value={editableEnglishHeading}
                              onChange={(e) => setEditableEnglishHeading(e.target.value)}
                              placeholder="English heading"
                            />
                            <EditablePointsList
                              language="english"
                              points={editableEnglishPoints}
                              onPointChange={handlePointChange}
                              onAddPoint={handleAddPointToList}
                              onDeletePoint={handleDeletePoint}
                            />
                          </div>

                          {/* Tetum column */}
                          <div>
                            <ColLabel>Tetum</ColLabel>
                            <StyledInput
                              type="text"
                              value={editableTetumHeading}
                              onChange={(e) => setEditableTetumHeading(e.target.value)}
                              placeholder="Tetum heading"
                            />
                            <EditablePointsList
                              language="tetum"
                              points={editableTetumPoints}
                              onPointChange={handlePointChange}
                              onAddPoint={handleAddPointToList}
                              onDeletePoint={handleDeletePoint}
                            />
                          </div>
                        </TwoCol>

                        <EditActions>
                          <SaveBtn onClick={handleSaveEdit} disabled={isLoading}>
                            <Save size={14} />
                            Save
                          </SaveBtn>
                          <CancelBtn onClick={handleCancelEdit} disabled={isLoading}>
                            <X size={14} />
                            Cancel
                          </CancelBtn>
                        </EditActions>
                      </>
                    ) : (
                      /* ── VIEW MODE ── */
                      <TwoCol>
                        <div>
                          <ColLabel>English</ColLabel>
                          <ContentDiv
                            dangerouslySetInnerHTML={{
                              __html: advisory.englishdescription || '<ul></ul>',
                            }}
                          />
                        </div>
                        <div>
                          <ColLabel>Tetum</ColLabel>
                          <ContentDiv
                            dangerouslySetInnerHTML={{
                              __html: advisory.tetumdescription || '<ul></ul>',
                            }}
                          />
                        </div>
                      </TwoCol>
                    )}
                  </BodyInner>
                </AccordionBody>
              </AccordionItem>
            );
          })}
        </AccordionList>
      )}

      {/* Inline loading overlay when data already present */}
      {isLoading && advisories.length > 0 && (
        <CenteredBox style={{ padding: '20px 0' }}>
          <Spinner />
        </CenteredBox>
      )}
    </PageWrapper>
  );
};

export default Lightningadvisory;
